import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Button, Select, MenuItem, Grid, Chip, CircularProgress, Divider, Avatar } from '@mui/material';
import { orderManagementQuery } from '../../Query/orderManagementQuery';

const statusOptions = [
  'Order Placed',
  'Accepted',
  'Rejected',
  'Preparing',
  'Dispatched',
  'Out for Delivery',
  'Arrived',
  'Delivered',
  'Cancelled',
];

const OrderEditPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const [itemStatus, setItemStatus] = useState<{ [key: string]: string }>({});
  const [itemDecision, setItemDecision] = useState<{ [key: string]: string }>({});

  const trackingOrder = location.state?.trackingOrder;

  // Fallback to fetch if not passed
  const { data, isLoading } = orderManagementQuery({ search: orderId });
  const order = trackingOrder || (data?.orders && data.orders[0]);

  if (!order || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const billing = order.invoice?.billingTo;
  const shipping = order.invoice?.shippingTo;
  const items = order.items || [];
  // Calculate subtotal using discounted price when available, otherwise original/unit price
  const subtotal = items.reduce((sum: number, item: any) => {
    const quantity = Number(item?.quantity ?? 0);
    // Prioritize discount prices over original price
    const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
    const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
    const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : (item?.unitPrice != null ? Number(item.unitPrice) : 0);
    const unitPrice = (discountedPrice != null && discountedPrice > 0) ? discountedPrice : originalPrice;
    // Include choices/add-ons if present (choicePrice multiplied by quantity)
    const choices = Array.isArray(item?.choices) ? item.choices : [];
    const choicesUnitTotal = choices.reduce((acc: number, choice: any) => {
      return acc + Number(choice?.choicePrice ?? 0);
    }, 0);
    const lineTotal = quantity * unitPrice + quantity * choicesUnitTotal;
    return sum + lineTotal;
  }, 0);
  const shippingRate = order?.shippingRate || 0;
  const vat = 0; // Assuming VAT is 0%
  const grandTotal = subtotal + shippingRate + vat;

  const handleStatusChange = (itemId: string, value: string) => {
    setItemStatus((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleDecision = (itemId: string, decision: string) => {
    setItemDecision((prev) => ({ ...prev, [itemId]: decision }));
  };

  return (
    <Box p={3}>
      {/* Billing and Shipping Address */}
      <Grid container spacing={3} mb={4}>
        {/* <Grid item xs={12} md={6}> */}
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={600} mb={1}>Billing Address</Typography>
              <Typography>{billing?.address}</Typography>
              <Typography>{billing?.city}</Typography>
              <Typography>{billing?.state}</Typography>
              <Typography>{billing?.country}</Typography>
            </CardContent>
          </Card>
        {/* </Grid> */}
        {/* <Grid item xs={12} md={6}> */}
          <Card variant="outlined">
            <CardContent>
              <Typography fontWeight={600} mb={1}>Shipping Address</Typography>
              <Typography>{shipping?.address}</Typography>
              <Typography>{shipping?.city}</Typography>
              <Typography>{shipping?.state}</Typography>
              <Typography>{shipping?.country}</Typography>
            </CardContent>
          </Card>
        </Grid>
      {/* </Grid> */}

      {/* Order ID */}
      <Typography variant="h6" fontWeight={600} mb={2}>
        Order #{order.orderId}
      </Typography>

      {/* Product Table Header */}
      <Grid container px={2} py={1} sx={{ backgroundColor: '#f9f9f9' }}>
        {/* <Grid item xs={5}> */}
          <Typography fontWeight={500}>Product</Typography>
          {/* </Grid> */}
        {/* <Grid item xs={2}> */}
          <Typography fontWeight={500}>SKU</Typography>
          {/* </Grid> */}
        {/* <Grid item xs={1}> */}
          <Typography fontWeight={500}>Qty</Typography>
        {/* </Grid> */}
        {/* <Grid item xs={2}> */}
          <Typography fontWeight={500}>Unit Price</Typography>
          {/* </Grid> */}
        {/* <Grid item xs={2}> */}
          <Typography fontWeight={500}>Total</Typography>
          {/* </Grid> */}
      </Grid>
      <Divider />

      {/* Product List */}
      {items.map((item: any, index: number) => (
        <Grid container px={2} py={2} alignItems="center" key={index} borderBottom="1px solid #eee">
          {/* <Grid item xs={5} display="flex" alignItems="center"> */}
            <Avatar
              src={item.imageUrl}
              alt={item.itemName}
              sx={{ width: 48, height: 48, mr: 2 }}
              variant="square"
            />
            <Box>
              <Typography>{item.itemName}</Typography>
              {/* Display choice names */}
              {item.choices && item.choices.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  {item.choices.map((choice: any, choiceIndex: number) => (
                    <Typography 
                      key={choiceIndex}
                      variant="caption" 
                      color="primary" 
                      sx={{ 
                        display: 'block',
                        fontStyle: 'italic',
                        fontSize: '0.75rem'
                      }}
                    >
                      + {choice.choiceName} {choice.choicePrice > 0 && `(JOD${choice.choicePrice})`}
                    </Typography>
                  ))}
                </Box>
              )}
              {/* Display meal information */}
              {item.mealId && item.mealName && (
                <Box sx={{ mt: 0.75, p: 0.75, backgroundColor: '#f0f7ff', borderRadius: 1 }}>
                  <Typography 
                    variant="caption" 
                    color="secondary" 
                    sx={{ 
                      display: 'block',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      mb: 0.25
                    }}
                  >
                    🍽️ Meal: {item.mealName} {item.mealPrice > 0 && `(JOD${item.mealPrice.toFixed(2)})`}
                  </Typography>
                  {item.mealChoiceGroups && item.mealChoiceGroups.length > 0 && (
                    <Box sx={{ pl: 0.5 }}>
                      {item.mealChoiceGroups.map((group: any, groupIndex: number) => (
                        <Typography 
                          key={groupIndex}
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            fontSize: '0.65rem',
                            color: 'text.secondary'
                          }}
                        >
                          {group.choiceGroupName}: {group.choices?.map((c: any) => c.choiceName).join(', ')}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              <Typography variant="body2" color="textSecondary">
                Delivery Date: {new Date(item.deliveryDate || order.orderDate).toLocaleDateString()}
              </Typography>
              <Box mt={1} display="flex" gap={1} alignItems="center">
                <Select
                  value={itemStatus[item.itemId] || item.status || ''}
                  onChange={(e) => handleStatusChange(item.itemId, e.target.value)}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
                <Button
                  variant={itemDecision[item.itemId] === 'Accepted' ? 'contained' : 'outlined'}
                  color="success"
                  size="small"
                  onClick={() => handleDecision(item.itemId, 'Accepted')}
                >
                  Accept
                </Button>
                <Button
                  variant={itemDecision[item.itemId] === 'Rejected' ? 'contained' : 'outlined'}
                  color="error"
                  size="small"
                  onClick={() => handleDecision(item.itemId, 'Rejected')}
                >
                  Reject
                </Button>
                {itemDecision[item.itemId] && (
                  <Chip
                    label={itemDecision[item.itemId]}
                    color={itemDecision[item.itemId] === 'Accepted' ? 'success' : 'error'}
                    size="small"
                  />
                )}
              </Box>
            </Box>
          {/* </Grid> */}
          {/* <Grid item xs={2}> */}
            <Typography>{item.itemCode}</Typography>
          {/* </Grid> */}
          {/* <Grid item xs={1}> */}
            <Typography>{item.quantity}</Typography>
          {/* </Grid> */}
          {/* <Grid item xs={2}> */}
            <Typography>
              JOD{(() => {
                // Prioritize discount prices over original price
                const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
                const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
                const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : (item?.unitPrice != null ? Number(item.unitPrice) : 0);
                const unitPrice = (discountedPrice != null && discountedPrice > 0) ? discountedPrice : originalPrice;
                return unitPrice.toFixed(2);
              })()}
            </Typography>
          {/* </Grid> */}
          {/* <Grid item xs={2}> */}
            <Typography fontWeight={600}>
              JOD{(() => {
                const quantity = Number(item?.quantity ?? 0);
                // Prioritize discount prices over original price
                const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
                const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
                const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : (item?.unitPrice != null ? Number(item.unitPrice) : 0);
                const unitPrice = (discountedPrice != null && discountedPrice > 0) ? discountedPrice : originalPrice;
                const choices = Array.isArray(item?.choices) ? item.choices : [];
                const choicesUnitTotal = choices.reduce((acc: number, choice: any) => {
                  return acc + Number(choice?.choicePrice ?? 0);
                }, 0);
                const lineTotal = quantity * unitPrice + quantity * choicesUnitTotal;
                return lineTotal.toFixed(2);
              })()}
            </Typography>
          {/* </Grid> */}
        </Grid>
      ))}

      {/* Order Summary */}
      <Box mt={3} px={2} textAlign="right">
        <Grid container justifyContent="flex-end">
          {/* <Grid item xs={12} sm={6} md={4}> */}
            <Box display="flex" justifyContent="space-between" py={0.5}>
              <Typography>Subtotal</Typography>
              <Typography>JOD{subtotal.toFixed(2)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" py={0.5}>
              <Typography>VAT (0%)</Typography>
              <Typography>JOD{vat.toFixed(2)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" py={0.5}>
              <Typography>Delivery Fee</Typography>
              <Typography>JOD{shippingRate.toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight={600}>Grand Total</Typography>
              <Typography fontWeight={600} fontSize="1.1rem">
                JOD{grandTotal.toFixed(2)}
              </Typography>
            </Box>
          {/* </Grid> */}
        </Grid>
      </Box>
    </Box>
  );
};

export default OrderEditPage; 